import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { tripService, Trip, getTripImageSrc, type TripImage } from '../../api/trips';
import type { Employee } from '../../api/Employee';
import type { HistoryTrip } from '../../api/history.trip';
import { historyTripService } from '../../api/history.trip';
import FilterOptions from './components/FilterOptions';
import {
  History,
  Search,
  RefreshCw,
  Calendar,
  Car,
  MapPin,
  User,
  Eye,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Clock,
  Route,
  ImageIcon,
  Filter,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const statusLabelMap: Record<string, string> = {
  aprovada: 'Aprovada',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
  rejeitada: 'Rejeitada',
};

interface TripWithVehicle extends Trip {
  employee?: Employee;
}

const FILTER_CATEGORIES = {
  Status: [
    { id: 'aprovada', label: 'Aprovada' },
    { id: 'em_andamento', label: 'Em andamento' },
    { id: 'concluida', label: 'Concluída' },
    { id: 'cancelada', label: 'Cancelada' },
    { id: 'rejeitada', label: 'Rejeitada' },
  ],
};

const HistoricoDeViagens = () => {
  const { setPageTitle } = useAuth();
  const [trips, setTrips] = useState<TripWithVehicle[]>([]);
  const [tripHistories, setTripHistories] = useState<HistoryTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});

  const [selectedTrip, setSelectedTrip] = useState<TripWithVehicle | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle('Histórico de viagens do setor');
  }, [setPageTitle]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allTrips, allHistories] = await Promise.all([
        tripService.getAll(),
        historyTripService.getAll(),
      ]);
      const nonPendingTrips = allTrips.filter((trip) => trip.status !== 'pendente');
      setTrips(nonPendingTrips);
      setTripHistories(allHistories);
    } catch (_err) {
      setError('Erro ao buscar histórico de viagens.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const getLatestHistory = useCallback(
    (tripId: number) =>
      tripHistories
        .filter((history) => history.trip_id === tripId)
        .sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )[0],
    [tripHistories],
  );

  const getTripHistoriesSorted = useCallback(
    (tripId: number) =>
      tripHistories
        .filter((h) => h.trip_id === tripId)
        .sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        ),
    [tripHistories],
  );

  const filteredTrips = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const selectedStatus = appliedFilters.Status || [];
    return trips.filter((trip) => {
      const latest = getLatestHistory(trip.id);
      const vehicleInfo = (latest?.vehicle_info || '').toLowerCase();
      const employeeInfo = (latest?.employee_info || '').toLowerCase();
      const matchesSearch =
        !term ||
        String(trip.id).includes(term) ||
        (trip.destination || '').toLowerCase().includes(term) ||
        (trip.purpose || '').toLowerCase().includes(term) ||
        vehicleInfo.includes(term) ||
        employeeInfo.includes(term) ||
        (trip.status || '').toLowerCase().includes(term);
      const matchesStatus =
        selectedStatus.length === 0 || selectedStatus.includes(trip.status);
      return matchesSearch && matchesStatus;
    });
  }, [trips, searchTerm, appliedFilters, getLatestHistory]);

  const totalPages = Math.max(1, Math.ceil(filteredTrips.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredTrips.length, currentPage, totalPages]);

  const { tripsPaginados, pageRange } = useMemo(() => {
    const total = filteredTrips.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      tripsPaginados: filteredTrips.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredTrips, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const formatStatus = (status?: string) =>
    statusLabelMap[status ?? ''] ?? status ?? '-';

  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'aprovada':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Aprovada
          </Badge>
        );
      case 'em_andamento':
        return (
          <Badge tone="blue" className="gap-1.5">
            <Route className="h-3 w-3 stroke-[2]" />
            Em andamento
          </Badge>
        );
      case 'concluida':
        return (
          <Badge tone="slate" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Concluída
          </Badge>
        );
      case 'cancelada':
        return (
          <Badge tone="slate" className="gap-1.5">
            <X className="h-3 w-3 stroke-[2]" />
            Cancelada
          </Badge>
        );
      case 'rejeitada':
        return (
          <Badge tone="red" className="gap-1.5">
            <XCircle className="h-3 w-3 stroke-[2]" />
            Rejeitada
          </Badge>
        );
      default:
        return (
          <Badge tone="amber" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            {formatStatus(status)}
          </Badge>
        );
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const ThumbnailCheckout = ({
    trip,
  }: {
    trip: TripWithVehicle;
  }) => {
    const checkoutImg = trip.images?.find(
      (img) => img.type === 'TRIP_CHECKOUT',
    ) as TripImage | undefined;
    const checkoutSrc = getTripImageSrc(checkoutImg);
    if (!checkoutSrc) {
      return (
        <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-[11.5px] font-medium text-slate-400 ring-1 ring-inset ring-slate-200">
          <ImageIcon className="h-3 w-3 stroke-[1.9] mr-1" />
          Sem foto
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setExpandedImage(checkoutSrc)}
        className="group relative block h-10 w-10 shrink-0 overflow-hidden rounded-lg border-2 border-white shadow-sm transition-all hover:z-10 hover:scale-110 hover:shadow-md"
        title="Clique para ampliar a foto de checkout"
      >
        <img src={checkoutSrc} alt="Checkout" className="h-full w-full object-cover" />
      </button>
    );
  };

  const renderVehicleCell = (trip: TripWithVehicle) => {
    const latest = getLatestHistory(trip.id);
    const lines = latest?.vehicle_info
      ? latest.vehicle_info.split('\n').filter(Boolean)
      : [];
    const mainLine = lines[0] || String(trip.vehicle_id);
    const subLine = lines.slice(1).join(' • ');
    return (
      <div
        className="min-w-0 w-full max-w-[260px] overflow-hidden"
        title={lines.join('\n')}
      >
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
            <Car className="h-[18px] w-[18px] stroke-[1.9]" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="block max-w-[200px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
              {mainLine}
            </span>
            {subLine && (
              <span className="block max-w-[200px] truncate mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500">
                {subLine}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmployeeCell = (trip: TripWithVehicle) => {
    const latest = getLatestHistory(trip.id);
    const lines = latest?.employee_info
      ? latest.employee_info.split('\n').filter(Boolean)
      : [];
    const mainLine = lines[0] || String(trip.employee_id);
    const subLine = lines.slice(1).join(' • ');
    return (
      <div
        className="min-w-0 w-full max-w-[260px] overflow-hidden"
        title={lines.join('\n')}
      >
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <User className="h-[18px] w-[18px] stroke-[1.9]" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="block max-w-[200px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
              {mainLine}
            </span>
            {subLine && (
              <span className="block max-w-[200px] truncate mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 tabular-nums">
                {subLine}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedTrip(null);
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Histórico de viagens do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Histórico — Viagens
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Registro completo de todas
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                as viagens do seu departamento
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button
              variant="secondary"
              onClick={() => setIsFilterOpen(true)}
              className="h-10 px-4"
            >
              <Filter className="h-4 w-4 stroke-[1.9]" />
              Filtros
            </Button>
            <Button
              variant="secondary"
              onClick={() => void fetchData()}
              className="h-10 px-4"
            >
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 inline-flex items-start gap-3 w-full">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 min-w-[44px] min-h-[44px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-500 via-blue-600 to-indigo-700 text-white shadow-[0_8px_22px_-10px_rgba(51,65,85,0.7)]">
                  <History className="h-[22px] w-[22px] stroke-[1.9]" />
                </div>
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                    Todas as viagens processadas
                  </h2>
                  <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                    Aprovadas, rejeitadas, em andamento, canceladas e concluídas
                  </p>
                </div>
              </div>
              <div className="relative w-full sm:w-[360px] shrink-0">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Buscar ID, destino, veículo, motorista..."
                  className="!pl-10 h-10"
                />
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && trips.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">
                  Carregando histórico...
                </span>
              </div>
            ) : (
              <ModernTable>
                <Thead>
                  <Tr>
                    <Th className="w-[11%] min-w-[130px]">Data Início</Th>
                    <Th className="w-[11%] min-w-[130px]">Data Fim</Th>
                    <Th className="w-[15%] min-w-[200px]">Veículo</Th>
                    <Th className="w-[12%] min-w-[160px]">Destino</Th>
                    <Th className="w-[15%] min-w-[220px]">Propósito</Th>
                    <Th className="w-[15%] min-w-[200px]">Funcionário</Th>
                    <Th className="w-[7%] min-w-[80px]">Checkout</Th>
                    <Th className="w-[9%] min-w-[120px]">Status</Th>
                    <Th className="w-[5%] min-w-[90px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tripsPaginados.length === 0 ? (
                    <Tr>
                      <Td colSpan={9}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <History className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">
                            Nenhuma viagem no histórico
                          </div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm || appliedFilters.Status?.length
                              ? 'Nenhum resultado para os filtros aplicados. Tente outros termos.'
                              : 'As viagens processadas aparecerão aqui automaticamente.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    tripsPaginados.map((trip) => {
                      return (
                        <Tr key={trip.id}>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(trip.start_date)}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                <Clock className="h-[13px] w-[13px] text-slate-400" />
                                {formatTime(trip.start_date)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(trip.end_date)}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                <Clock className="h-[13px] w-[13px] text-slate-400" />
                                {formatTime(trip.end_date)}
                              </div>
                            </div>
                          </Td>
                          <Td>{renderVehicleCell(trip)}</Td>
                          <Td>
                            <div
                              className="min-w-0 w-full max-w-[220px] overflow-hidden"
                              title={trip.destination || '-'}
                            >
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                                  <MapPin className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block max-w-[160px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                    {trip.destination || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[360px] overflow-hidden">
                              <p
                                className="text-[13px] font-medium leading-snug text-slate-800 line-clamp-2"
                                title={trip.purpose || '-'}
                              >
                                {trip.purpose || '-'}
                              </p>
                            </div>
                          </Td>
                          <Td>{renderEmployeeCell(trip)}</Td>
                          <Td>
                            <div className="flex items-center justify-center">
                              <ThumbnailCheckout trip={trip} />
                            </div>
                          </Td>
                          <Td>
                            <div className="flex items-center justify-start">
                              {renderStatusBadge(trip.status)}
                            </div>
                          </Td>
                          <Td>
                            <div className="flex items-center justify-center gap-1.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedTrip(trip);
                                  setIsViewModalOpen(true);
                                }}
                                title="Visualizar detalhes"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 hover:ring-indigo-200"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.start}
                  </span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.end}
                  </span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.total}
                  </span>
                  {' viagem(ns)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1 ${
                            n === currentPage
                              ? 'bg-indigo-600 text-white shadow-[0_4px_14px_-4px_rgba(79,70,229,0.55)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700'
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isViewModalOpen && selectedTrip && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-4xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 min-w-[40px] min-h-[40px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-500 via-blue-600 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(51,65,85,0.7)]">
                <Eye className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes da Viagem #{selectedTrip.id}
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Visualize todos os dados e o histórico de ações
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Dados da Viagem
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Destino</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {selectedTrip.destination || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      Status atual
                    </span>
                    <div className="flex items-center">
                      {renderStatusBadge(selectedTrip.status)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      Data e Hora de Início
                    </span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">
                      {formatDateTime(selectedTrip.start_date)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      Data e Hora de Término
                    </span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">
                      {formatDateTime(selectedTrip.end_date)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <span className="text-xs font-semibold text-slate-500">
                      Propósito / Motivo
                    </span>
                    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[64px]">
                      {selectedTrip.purpose || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                  Equipe e Veículo
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[56px]">
                      {(getLatestHistory(selectedTrip.id)?.vehicle_info ||
                        String(selectedTrip.vehicle_id))
                        .split('\n')
                        .map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Funcionário</span>
                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[56px]">
                      {(getLatestHistory(selectedTrip.id)?.employee_info ||
                        String(selectedTrip.employee_id))
                        .split('\n')
                        .map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {(selectedTrip.status === 'rejeitada' ||
              selectedTrip.status === 'cancelada') &&
              selectedTrip.cancellation_reason && (
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-rose-500 to-red-600" />
                    <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-rose-700">
                      Motivo da{' '}
                      {selectedTrip.status === 'rejeitada' ? 'Rejeição' : 'Cancelamento'}
                    </h3>
                  </div>
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-sm text-rose-800 whitespace-pre-wrap">
                    {selectedTrip.cancellation_reason}
                  </div>
                </div>
              )}

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-500 to-orange-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-amber-700">
                  Foto de Checkout
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                {(() => {
                  const checkoutImgObj = selectedTrip.images?.find(
                    (img) => img.type === 'TRIP_CHECKOUT',
                  );
                  const checkoutSrc = getTripImageSrc(checkoutImgObj);
                  return checkoutSrc ? (
                    <button
                      type="button"
                      onClick={() => setExpandedImage(checkoutSrc)}
                      className="group relative block w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 shadow-sm transition-all hover:shadow-lg"
                      title="Clique para ampliar"
                    >
                      <div className="aspect-video w-full bg-slate-50">
                        <img
                          src={checkoutSrc}
                          alt="Checkout da viagem"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </button>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-slate-400">
                      <ImageIcon className="h-10 w-10 stroke-[1.7]" />
                      <span className="text-[13px] font-semibold">
                        Nenhuma foto de checkout disponível
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-indigo-500 to-purple-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-indigo-700">
                  Histórico de Ações
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                {getTripHistoriesSorted(selectedTrip.id).length === 0 ? (
                  <p className="text-center py-8 text-slate-400 text-sm">
                    Nenhum registro de ação para esta viagem.
                  </p>
                ) : (
                  <ol className="relative border-l border-slate-200 ml-2.5 space-y-5">
                    {getTripHistoriesSorted(selectedTrip.id).map((h) => (
                      <li key={h.id} className="pl-5">
                        <span className="absolute -left-[7px] mt-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 ring-4 ring-white" />
                        <div className="flex flex-col gap-0.5">
                          <div className="text-[13.5px] font-bold leading-tight text-slate-900">
                            {h.action}
                          </div>
                          {h.details && (
                            <p className="text-[12.5px] font-medium leading-snug text-slate-600 mt-0.5 whitespace-pre-wrap">
                              {h.details}
                            </p>
                          )}
                          <div className="mt-1 inline-flex items-center gap-1 text-[11.5px] font-semibold text-slate-400 tabular-nums">
                            <Clock className="h-3 w-3 stroke-[1.9]" />
                            {formatDateTime(h.created_at)}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {expandedImage && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 animate-[fadeIn_0.15s_ease-out]"
          onClick={() => setExpandedImage(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-inset ring-white/20 backdrop-blur-sm transition-all hover:bg-white/20 hover:scale-105"
            onClick={() => setExpandedImage(null)}
            aria-label="Fechar"
          >
            <X className="h-6 w-6 stroke-[2]" />
          </button>
          <img
            src={expandedImage}
            alt="Foto ampliada"
            className="max-h-[90vh] max-w-[90vw] rounded-2xl border-4 border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={(filters) => {
          setAppliedFilters(filters);
          setCurrentPage(1);
        }}
        filterCategories={FILTER_CATEGORIES}
        initialFilters={appliedFilters}
      />
    </div>
  );
};

export default HistoricoDeViagens;
